import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

// UX Design Breakpoints and Naming Structure (guide only do not edit):
// Mobile:
// 360px: 140 MobileXSmall (This is below our minimum, but it’s a common Android size, adding it here just in case)
// 375px: 130 MobileSmall
// 480px: 120 MobileLarge
// 600px: 110 MobileXLarge

// Tablet:
// 768px: 100 TabletXSmall
// 834px: 90 TabletSmall
// 900px: 80 TabletMedium
// 960px: 70 TabletLarge
// 1024px: 60 TabletXLarge

// Desktop:
// 1280px: 50 DesktopSmall
// 1366px: 40 DesktopMedium
// 1440px: 30 DesktopLarge
// 1600px: 20 DesktopXLarge
// 1920px: 10 DesktopXXLarge

/**
 * Screen descriptions
 * export const SCREEN_SIZE_MOBILE_LARGE = 'MobileLarge';
 * export const SCREEN_SIZE_MOBILE_XLARGE = 'MobileXLarge';
 * export const SCREEN_SIZE_TABLET_XSMALL = 'TabletXSmall';
 * export const SCREEN_SIZE_TABLET_SMALL = 'TabletSmall';
 * export const SCREEN_SIZE_TABLET_MEDIUM = 'TabletMedium';
 * export const SCREEN_SIZE_DESKTOP_SMALL = 'DesktopSmall';
 *
 * Add to these SCREEN_WIDTH_QUERIES as needed
 * export const SCREEN_WIDTH_QUERIES = [
 *   { query: '(max-width: 479px)', value: 120, label: SCREEN_SIZE_MOBILE_LARGE },
 *   { query: '(max-width: 599px)', value: 110, label: SCREEN_SIZE_MOBILE_XLARGE },
 *   { query: '(max-width: 767px)', value: 100, label: SCREEN_SIZE_TABLET_XSMALL },
 *   { query: '(max-width: 833px)', value: 90, label: SCREEN_SIZE_TABLET_SMALL },
 *   { query: '(max-width: 899px)', value: 80, label: SCREEN_SIZE_TABLET_MEDIUM },
 *   { query: '(max-width: 1279px)', value: 50, label: SCREEN_SIZE_DESKTOP_SMALL },
 * ];
 */

/**
 * Custom hook that uses media queries to determine screen size and updates the screen size label in the Redux store.
 * 
 * This hook listens for changes in screen width, evaluates media queries, and dispatches an action with the appropriate
 * screen size value based on the current screen width. The action creator is passed as an argument, and the hook uses
 * the `useDispatch` hook from `react-redux` to send the updates to the Redux store.
 * 
 * **Example Usage**:
 * 
 * ```javascript
 * const SCREEN_WIDTH_QUERIES = [
 *   { query: '(max-width: 599px)', value: 110, label: SCREEN_SIZE_MOBILE_XLARGE },
 *   { query: '(max-width: 767px)', value: 100, label: SCREEN_SIZE_TABLET_XSMALL },
 * ];
 * 
 * const MyComponent = () => {
 *   useResponsiveValueRedux(updateScreenSizeAction, SCREEN_WIDTH_QUERIES);
 *   return <div>Responsive component</div>;
 * };
 * ```

 *
 * @param {function} updateScreenSizeAction - Action creator that updates the screen size label in the Redux store.
 * @param {Array<{ query: string, value: any }>} queries - Array of media query objects containing the media query string and its corresponding value.
 */

const useResponsiveValueRedux = (updateScreenSizeAction, queries) => {
  const dispatch = useDispatch();

  useEffect(() => {
    const evaluateMediaQueries = () => {
      const activeQuery = queries.find(
        (q) => window.matchMedia(q.query).matches
      );

      dispatch(updateScreenSizeAction(activeQuery ? activeQuery.value : 0));
    };

    window.addEventListener('resize', evaluateMediaQueries);
    evaluateMediaQueries();

    return () => {
      window.removeEventListener('resize', evaluateMediaQueries);
    };
  }, [dispatch, updateScreenSizeAction]);
};

/**
 * Custom hook that uses media queries to evaluate screen sizes and update a local or external state with the corresponding value.
 *
 * This hook takes a `setState` function and an array of media query objects, where each object should contain:
 * - `query`: The media query string to evaluate.
 * - `value`: The value to set when the query matches.
 *
 * The hook listens for window resize events and evaluates the media queries to determine the active screen size,
 * updating the state with the matching value.
 *
 * @param {function} setState - Function to update the state with the value of the matching media query.
 * @param {Array<{ query: string, value: any }>} queries - Array of media query objects containing the media query string and its corresponding value.
 *
 * @example
 *
 * const SCREEN_WIDTH_QUERIES = [
 *   { query: '(max-width: 599px)', value: 110, label: SCREEN_SIZE_MOBILE_XLARGE },
 *   { query: '(max-width: 833px)', value: 90, label: SCREEN_SIZE_TABLET_SMALL },
 * ];
 *
 * const MyComponent = () => {
 *   const [responsiveValue, setResponsiveValue] = useState(0);
 *
 *   useResponsiveValueState(setResponsiveValue, SCREEN_WIDTH_QUERIES);
 *
 *   return <div>Current screen size: {responsiveValue}</div>;
 * };
 */

const useResponsiveValueState = (setState, queries) => {
  useEffect(() => {
    const evaluateMediaQueries = () => {
      const activeQuery = queries.find(
        (q) => window.matchMedia(q.query).matches
      );

      setState(activeQuery ? activeQuery.value : 0);
    };

    window.addEventListener('resize', evaluateMediaQueries);
    evaluateMediaQueries();

    return () => {
      window.removeEventListener('resize', evaluateMediaQueries);
    };
  }, [setState]);
};

export { useResponsiveValueRedux, useResponsiveValueState };
